/**
 * ToastProvider - Global toast context and provider
 *
 * Wrap your app with this provider to enable toasts anywhere via useToast hook.
 *
 * @example
 * // In App.tsx or main.tsx
 * <ToastProvider>
 *   <App />
 * </ToastProvider>
 *
 * // In any component
 * const { showSuccessToast, showErrorToast } = useToast()
 * showSuccessToast('Saved!')
 */

import * as React from 'react'
import * as ToastPrimitive from '@radix-ui/react-toast'
import { Toast } from './Toast'
import * as styles from './Toast.css'
import { ToastContext } from './useToast'
import type { ToastData } from './useToast'

// =============================================================================
// Provider
// =============================================================================

interface ToastProviderProps {
  children: React.ReactNode
  /** Default duration in ms (default: 5000) */
  duration?: number
  /** Swipe direction to dismiss (default: "right") */
  swipeDirection?: 'right' | 'left' | 'up' | 'down'
}

export function ToastProvider({
  children,
  duration = 5000,
  swipeDirection = 'right',
}: ToastProviderProps) {
  const [toasts, setToasts] = React.useState<ToastData[]>([])

  const addToast = React.useCallback((toast: Omit<ToastData, 'id'>) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`
    setToasts((prev) => [...prev, { ...toast, id }])
    return id
  }, [])

  const removeToast = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const showSuccessToast = React.useCallback(
    (description: string, title?: string) => addToast({ description, title, variant: 'success' }),
    [addToast]
  )

  const showErrorToast = React.useCallback(
    (description: string, title?: string) => addToast({ description, title, variant: 'error' }),
    [addToast]
  )

  const showInfoToast = React.useCallback(
    (description: string, title?: string) => addToast({ description, title, variant: 'info' }),
    [addToast]
  )

  const showWarningToast = React.useCallback(
    (description: string, title?: string) => addToast({ description, title, variant: 'warning' }),
    [addToast]
  )

  const value = React.useMemo(
    () => ({ toasts, addToast, removeToast, showSuccessToast, showErrorToast, showInfoToast, showWarningToast }),
    [toasts, addToast, removeToast, showSuccessToast, showErrorToast, showInfoToast, showWarningToast]
  )

  return (
    <ToastContext.Provider value={value}>
      <ToastPrimitive.Provider
        duration={duration}
        swipeDirection={swipeDirection}
        label="Notifications"
      >
        {children}
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            variant={toast.variant}
            {...(toast.duration !== undefined && { duration: toast.duration })}
            onOpenChange={(open) => {
              if (!open) removeToast(toast.id)
            }}
          >
            {toast.title && <Toast.Title>{toast.title}</Toast.Title>}
            <Toast.Description>{toast.description}</Toast.Description>
          </Toast>
        ))}
        <ToastPrimitive.Viewport className={styles.viewport} />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  )
}
